/**
 * Normalizers for project management / work items API responses.
 * Used by projectManagementThunk – keep thunk file for API calls only.
 */

function extractProjectTagIds(idsField, tagsLegacy) {
  if (Array.isArray(idsField) && idsField.length) {
    return idsField.map((x) => String(x)).filter(Boolean);
  }
  if (Array.isArray(tagsLegacy) && tagsLegacy.length && typeof tagsLegacy[0] === "object") {
    return tagsLegacy
      .map((t) => String(t.id ?? t.listItemId ?? t.systemListItemId ?? ""))
      .filter(Boolean);
  }
  return [];
}

/**
 * Unwrap GET /projects/:id body (axios `response.data` may be `{ data: project }` or nested `{ data: { data } }`).
 */
export function unwrapProjectDetailPayload(raw) {
  if (raw == null || typeof raw !== "object") return null;
  let o = raw;
  if (o.data != null && typeof o.data === "object" && !Array.isArray(o.data)) {
    o = o.data;
    if (o.data != null && typeof o.data === "object" && !Array.isArray(o.data)) {
      o = o.data;
    }
  }
  return o;
}

/**
 * Normalize API project to UI shape (list expects clientName, projectName, receivedDate, status)
 * API: { id, name, projectKey, startDate, endDate, client: { name }, status, ... }
 */
export const normalizeProjectFromApi = (p) => ({
  ...p,
  clientName: p.client?.name ?? p.clientName,
  projectName: p.name ?? p.projectName,
  receivedDate: p.startDate ?? p.receivedDate,
  status: p.status ?? "ACTIVE",
  billingTags: p.clientTags ?? p.billingTags ?? [],
  vendorTags: p.vendorTags ?? [],
  clientTagIds: extractProjectTagIds(p.clientTagIds, p.clientTags ?? p.billingTags),
  vendorTagIds: extractProjectTagIds(p.vendorTagIds, p.vendorTags),
  clientNotes: p.clientNotes ?? "",
  vendorNotes: p.vendorNotes ?? "",
});

/**
 * Map UI status to API enum for filter (Active → ACTIVE, etc.)
 */
export const toProjectStatusApi = (value) => {
  if (!value || String(value).trim() === "") return undefined;
  const map = {
    Active: "ACTIVE",
    Completed: "COMPLETED",
    "On Hold": "ON_HOLD",
    Archived: "ARCHIVED",
    Archive: "ARCHIVED",
  };
  return map[value] ?? String(value).toUpperCase().replace(/\s+/g, "_");
};

/**
 * Normalize API work item to UI shape for IssueList/KanbanBoard.
 * API: { id, itemKey, title, name, type: { name }, status: { name, category }, priority: { name }, assignee, ... }
 * UI expects: issueKey, type, status, priority, assignee (string), reporter (string), projectName, clientName, etc.
 * Status uses API name as-is (e.g. "To Do", "In Progress", "Blocked", "Done").
 */
export const normalizeWorkItemFromApi = (item) => {
  if (!item || typeof item !== "object") return item;
  const statusName = item.status?.name ?? item.status?.category ?? "";
  const status = statusName ? String(statusName).trim() : "";
  const statusId = item.status?.id ?? item.statusId ?? null;
  /** Flat category for status-category filters (global/my lists); API often only sends `status.category`. */
  const statusCategoryRaw =
    item.statusCategory ??
    (typeof item.status === "object" && item.status != null
      ? (item.status.category ?? item.status.statusCategory)
      : null) ??
    "";
  const statusCategory =
    statusCategoryRaw != null && String(statusCategoryRaw).trim() !== ""
      ? String(statusCategoryRaw).trim().toUpperCase().replace(/\s+/g, "_")
      : "";
  const assigneeDisplay =
    item.assignee == null
      ? ""
      : typeof item.assignee === "string"
        ? item.assignee
        : item.assignee?.name
          ? item.assignee.name
          : [item.assignee?.firstName, item.assignee?.lastName].filter(Boolean).join(" ").trim() ||
            "N/A";
  const assignee = assigneeDisplay || null;
  const assigneeId = item.assigneeId ?? item.assignee?.id ?? null;
  const reporterNameFromApi =
    item.reporterName != null && String(item.reporterName).trim() !== ""
      ? String(item.reporterName).trim()
      : "";
  const reporterFromNested =
    item.reporter == null
      ? ""
      : typeof item.reporter === "string"
        ? String(item.reporter).trim()
        : item.reporter?.name
          ? String(item.reporter.name).trim()
          : [item.reporter?.firstName, item.reporter?.lastName].filter(Boolean).join(" ").trim() ||
            (item.reporter?.email ? String(item.reporter.email).trim() : "");
  const reporterDisplay = (reporterNameFromApi || reporterFromNested || "").trim();
  const reporter = reporterDisplay || null;
  const reporterId =
    item.reporterId ??
    (typeof item.reporter === "object" && item.reporter != null ? item.reporter.id : null) ??
    null;
  const priorityId =
    item.priorityId ??
    (typeof item.priority === "object" && item.priority != null ? item.priority.id : null) ??
    null;
  /** List/detail: API may return `parent` as nested { id, itemKey, title, ... } */
  const parentTitle =
    typeof item.parent === "object" && item.parent != null
      ? String(item.parent.title ?? item.parent.itemKey ?? "").trim()
      : item.parent != null && typeof item.parent !== "object"
        ? String(item.parent).trim()
        : "";
  /** Filter dropdowns use system-list ids; list rows expose display `type` — keep id for filter matching. */
  const typeId =
    typeof item.type === "object" && item.type != null
      ? (item.type.id ?? item.typeId ?? null)
      : (item.typeId ?? null);
  return {
    ...item,
    assigneeId,
    issueKey: item.itemKey ?? item.issueKey ?? "",
    typeId,
    type: item.type?.name ?? item.type ?? "",
    status,
    statusId,
    statusCategory,
    priorityId,
    priority:
      typeof item.priority === "object" && item.priority != null
        ? (item.priority.name ?? "")
        : (item.priority ?? ""),
    assignee,
    reporter,
    reporterId,
    projectName:
      item.projectName ??
      item.project_name ??
      item.project?.name ??
      item.project?.projectName ??
      "",
    clientName: item.client?.name ?? item.client?.clientName ?? item.clientName ?? "",
    // Global /work-items list may expose project id as projectId, project_id, or nested project.id
    projectId: item.project?.id ?? item.projectId ?? item.project_id ?? null,
    title: String(item.title ?? item.summary ?? item.name ?? "").trim(),
    description: item.description ?? "",
    estimate: item.originalEstimate ?? item.originalEstimateMinutes ?? item.estimate ?? "",
    storyPoints: item.storyPoints != null ? String(item.storyPoints) : "",
    created_at: item.created_at ?? item.createdAt ?? null,
    updated_at: item.updated_at ?? item.updatedAt ?? null,
    createdAt: item.created_at ?? item.createdAt ?? null,
    updatedAt: item.updated_at ?? item.updatedAt ?? null,
    parentTitle,
  };
};
